import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Box, Container, Paper, Stack, Tabs, useMediaQuery, useTheme } from "@mui/material"

import { AuthorResult } from "interface/author"
import { KojinResult } from "interface/kojin"

import { useUserSession } from "features/session/context"

import { KojinDownloadDialog } from "features/kojin/dialog/download"
import { KojinPreviewDialog } from "features/kojin/dialog/preview"
import { SisetuPreviewDialog } from "features/sisetu/dialog/preview"
import { ArticlePreviewDialog } from "features/article/dialog/preview"
import { TrialPreviewDialog } from "features/trial/dialog/preview"

import { useKojinPreview } from "./container"
import { KojinHeader } from "./components/header"
import { KojinPreviewTab } from './components/tab'
import { KojinBasicPage } from "./components/basicPage"
import { KojinEvaluationPage } from "./components/evaluationPage"
import { KojinSummaryPage } from "./components/summaryPage"
import { KojinJournalPage } from "./components/journalPage"
import { KojinCareNetPage } from "./components/careNetPage"

import { useKojinSummary } from "./components/summaryPage/components/kojinSummary"

const KojinPreviewTabPageProps = (value: string) => ({
  role: 'tabpanel',
  id: `kojin-preview-${value}`,
  'aria-labelledby': `kojin-preview-tabpanel-${value}`,
})

type KojinPreviewBodyProps = {
	kojin: KojinResult | null
	author: AuthorResult | null
}
export const KojinPreviewBody = ({
	kojin: defaultKojin,
	author: defaultAuthor
}: KojinPreviewBodyProps) => {
	const session = useUserSession()
	const [params, setParams] = useSearchParams()
	const theme = useTheme()
	const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))

	const [tabPage, setTabPage] = useState<string>(
		() => {
			const page = params.get('page')
			if (page === 'basic') return 'basic'
			if (page === 'summary') return 'summary'
			if (page === 'journal') return 'journal'
			if (page === 'carenet') return 'carenet'
			return 'basic'
		}			
	)
	useEffect(
		() => {
			setParams(prev => {
				prev.set('page', tabPage)
				return prev
			})
		},
		[tabPage, setParams]
	)

	const container = useKojinPreview({
    defaultKojin,
    defaultAuthor
  })
  const {
    modal, closeModal,
    kojin, articleCount,
    /*onSisetuOpen,*/ selectedSisetuCode,
    /*onKojinOpen,*/ selectedKojinCode,
    /*onAuthorOpen,*/ selectedAuthorId,
    /*onArticleOpen,*/ selectedArticleId, articleList,
    /*onTrialOpen,*/ selectedTrialId, trialList,
		downloading, onDownload
  } = container

  const summary = useKojinSummary(kojin)

	return (
		<Box
			flexGrow={1}
			boxSizing="border-box"
			width="100%"
			overflow="hidden"
			position="relative"
		>
			<Container
				maxWidth={isSmallMedia ? undefined : "xl"}
				sx={{
					height: '100%',
					padding: isSmallMedia ? 0 : 2
				}}
			>
				<Paper
					square={isSmallMedia}
					sx={{
						height: "100%",
						width: "100%",
						overflow: "hidden",
					}}
				>
					<Stack direction="column" height='100%' overflow='hidden'>
						<Box padding={1}>
							<KojinHeader container={container} tabName={tabPage}/>
						</Box>
						<Tabs
							value={tabPage}
							onChange={(_, newValue) => setTabPage(newValue)}
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
		            paddingX: isSmallMedia ? 0 : 1
							}}
							//variant="scrollable"
							//scrollButtons="auto"
							//allowScrollButtonsMobile
							aria-label="kojin-preview-tabs"
						>
							<KojinPreviewTab label="基本情報" value="basic" />
							{container.evaluationForm && (
								<KojinPreviewTab label="評価" value="evaluation" />
							)}
							{summary.isValid && (
								<KojinPreviewTab label="サマリー" value="summary" />
							)}
							{(articleCount > 0) && (
								<KojinPreviewTab
									label="論文"
									value="journal"
									badge={articleCount}
								/>
							)}
							{session?.isCareNet && kojin?.personalId && (
								<KojinPreviewTab
									label="閲覧履歴"
									value="carenet"
									disabled={kojin?.personalId ? false : true}
									badge={kojin?.mrpStats?.rank.name}
								/>
							)}
						</Tabs>

						<Box flexGrow={1} overflow='auto'>
							{tabPage === 'basic' && (
								<KojinBasicPage
									key="kojin-preview-basic"
									{...KojinPreviewTabPageProps('basic')}
									container={container}
									isSmallMedia={isSmallMedia}
								/>
							)}
							{tabPage === 'evaluation' && (
								<KojinEvaluationPage
									key="kojin-preview-evaluation"
									{...KojinPreviewTabPageProps('evaluation')}
									container={container}
								/>
							)}
							{tabPage === 'summary' && (
								<KojinSummaryPage
									key="kojin-preview-summary"
									{...KojinPreviewTabPageProps('summary')}
									container={summary}
								/>
							)}
							{tabPage === 'journal' && (
								<KojinJournalPage
									key="kojin-preview-journal"
									{...KojinPreviewTabPageProps('journal')}
									container={container}
									isSmallMedia={isSmallMedia}
								/>
							)}
							{tabPage === 'carenet' && (
								<KojinCareNetPage
									key="kojin-preview-carenet"
									{...KojinPreviewTabPageProps('carenet')}
									container={container}
									isSmallMedia={isSmallMedia}
								/>
							)}
						</Box>

						{(modal === 'download' && kojin) ? (
							<KojinDownloadDialog
								downloading={downloading}
								defaultTitle={kojin.isiName ?? kojin.isiNameKana ?? 'No name'}
								onDownload={onDownload}
								onClose={closeModal}
							/>
						) : (selectedKojinCode || selectedAuthorId) ? (
							<KojinPreviewDialog
								kojinCode={selectedKojinCode}
								authorId={selectedAuthorId}
								onClose={closeModal}
							/>
						) : selectedSisetuCode ? (
							<SisetuPreviewDialog
								sisetuCode={selectedSisetuCode}
								onClose={closeModal}
							/>
						) : selectedArticleId ? (
							<ArticlePreviewDialog
								articleList={articleList}
								articleId={selectedArticleId}
								targetKojinCode={kojin?.kojinCode}
								targetAuthorId={kojin?.authorId}
								onClose={closeModal}
							/>
						) : selectedTrialId ? (
							<TrialPreviewDialog
								trialList={trialList}
								trialId={selectedTrialId}
								targetKojinCode={kojin?.kojinCode}
								onClose={closeModal}
							/>
						) : <></>}
					</Stack>
				</Paper>
			</Container>
		</Box>
	)
}